import { DeleteOutline, Edit, GroupTwoTone } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ConfirmModal,
    ISupportCase,
    ISupportee,
    PermissionKeys,
    Table,
    useHasPermission,
    Widget,
} from '../../../shared';
import { Gender, SupporteeRelation } from '../../enums';
import { useDeleteSupportee } from '../../hooks';
import { SaveSupporteeModal } from '../save-supportee-modal/save-supportee-modal.component';
import { FullName } from '../full-name/full-name.component';
import { getLanguage, getNationality } from '../../../shared/utils/origins.util';
import { formatSocialSecurityNumber } from '../../utils/format-social-security-number.util';

interface Props {
    supportCase: ISupportCase;
}

export const Supportees: FC<Props> = ({ supportCase }) => {
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.SUPPORT_CASES_WRITE);
    const { t } = useTranslation();
    const { mutateAsync: deleteSupportee } = useDeleteSupportee(supportCase.id);
    const [supporteeModal, setSupporteeModal] = useState<{ show: boolean; supportee: ISupportee | null }>({
        show: false,
        supportee: null,
    });

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: t('nameLabel'),
            minWidth: 180,
            flex: 1,
            sortable: false,
            hideable: false,
            renderCell: ({ row }: GridCellParams<ISupportee>) => <FullName data={row} />,
        },
        { field: 'dateOfBirth', headerName: t('dateOfBirthLabel'), flex: 1, minWidth: 100, sortable: false },
        {
            field: 'relation',
            headerName: t('relation'),
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: ({ row }: GridCellParams<ISupportee>) => <RelationCell supportee={row} />,
        },
        {
            field: 'nationalSecurityNumber',
            headerName: t('nationalSecurityNumberLabel'),
            flex: 1,
            minWidth: 100,
            sortable: false,
            valueFormatter: (value: string) => formatSocialSecurityNumber(value),
        },
        {
            field: 'gender',
            headerName: t('genderLabel'),
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: ({ row }: GridCellParams<ISupportee>) => <GenderCell supportee={row} />,
        },
        {
            field: 'language',
            headerName: t('languageLabel'),
            flex: 1,
            minWidth: 100,
            sortable: false,
            valueFormatter: (language: string) => getLanguage(language)?.label || '',
        },
        {
            field: 'nationality',
            headerName: t('nationalityLabel'),
            flex: 1,
            minWidth: 100,
            sortable: false,
            valueFormatter: (nationality: string) => getNationality(nationality)?.label || '',
        },
        {
            field: 'dependencyPercentage',
            headerName: t('dependencyPercentage'),
            flex: 1,
            minWidth: 50,
            sortable: false,
            valueFormatter: (value: number) => (value === 100 ? '' : value),
        },
        ...(hasWritePermission
            ? [
                  {
                      field: 'actions',
                      headerName: t('actions'),
                      sortable: false,
                      width: 100,
                      align: 'right' as const,
                      renderCell: ({ row }: GridCellParams<ISupportee>) => (
                          <ActionsCell
                              onEdit={() => setSupporteeModal({ show: true, supportee: row })}
                              supportee={row}
                              supportCase={supportCase}
                              onDelete={() => row.id && deleteSupportee(row.id)}
                          />
                      ),
                  },
              ]
            : []),
    ];

    return (
        <Widget
            icon={<GroupTwoTone color="primary" />}
            title={t('supportees')}
            actions={
                <>
                    {hasWritePermission && (
                        <Button
                            variant="outlined"
                            color="primary"
                            data-testid="add-supportee"
                            onClick={() => setSupporteeModal({ show: true, supportee: null })}
                        >
                            + {t('addSupportee')}
                        </Button>
                    )}
                </>
            }
            hasTable
        >
            <Table
                rows={supportCase.supportees || []}
                columns={columns}
                hideFooter
                prefix="supportees"
                disableColumnMenu={false}
                paginationMode="client"
            />

            <SaveSupporteeModal
                open={supporteeModal.show}
                supportCaseId={supportCase.id}
                supportee={supporteeModal.supportee}
                handleClose={() => setSupporteeModal({ show: false, supportee: null })}
            />
        </Widget>
    );
};

function isValidRelation(relation: string | null | undefined): relation is SupporteeRelation {
    return Object.values(SupporteeRelation).includes(relation as SupporteeRelation);
}

function RelationCell({ supportee }: { supportee: ISupportee }) {
    const { t } = useTranslation();
    return <>{isValidRelation(supportee.relation) ? t(supportee.relation.toLowerCase()) : supportee.relation}</>;
}

function isValidGender(gender: string | null | undefined): gender is Gender {
    return Object.values(Gender).includes(gender as Gender);
}

function GenderCell({ supportee }: { supportee: ISupportee }) {
    const { t } = useTranslation();
    return <>{isValidGender(supportee.gender) ? t(supportee.gender.toLowerCase()) : supportee.gender}</>;
}

function ActionsCell({
    onEdit,
    supportee,
    supportCase,
    onDelete,
}: {
    supportee: ISupportee;
    supportCase: ISupportCase;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { t } = useTranslation();

    return (
        <>
            <IconButton onClick={onEdit} data-testid={`edit-${supportee.id}`}>
                <Edit />
            </IconButton>

            <ConfirmModal
                onConfirm={onDelete}
                button={
                    <IconButton
                        aria-label="delete"
                        data-testid={`delete-${supportee.id}`}
                        disabled={supportee.id === supportCase.primarySupportee.id}
                    >
                        <DeleteOutline />
                    </IconButton>
                }
                title={t('supporteeDeleteWarningTitle')}
                text={t('supporteeDeleteWarningText')}
            />
        </>
    );
}
